import { acceptHMRUpdate, defineStore } from 'pinia'
import type { Contribution, TradeProfile, User } from '~/types/prisma'

export const useAuthStore = defineStore('auth', () => {
  const isAuthenticated = ref(false)
  const lastCheckTime = ref()

  const user = ref<User & {
    tradeProfile: (TradeProfile & {
      Contribution: Contribution[]
    })[]
  } & {
    carts: any
  } | null>(null)

  const isAdmin = computed(() =>
    user.value?.systemRoles?.some((r: any) => r.level >= 4) ?? false
  )

  const isViewer = computed(() =>
    user.value?.systemRoles?.some((r: any) => r.level >= 2) ?? false
  )

  const eventRoleLevel = computed(() =>
    user.value?.participant?.eventRole?.level ?? -1
  )

  const isJudge = computed(() => eventRoleLevel.value >= 50)

  const isLeader = computed(() => eventRoleLevel.value >= 10)

  const isSponsor = computed(() => eventRoleLevel.value >= 20)

  const isUser = computed(() => eventRoleLevel.value >= 0)

  const hasEventRole = computed(() =>
    user.value?.participant?.eventRole != null
  )

  const defaultUserPage = computed(() => {
    if (isViewer.value) return '/admin'
    if (isJudge.value) return '/judges'
    if (hasEventRole.value) return '/dashboard'
    if (user.value) return '/dashboard'
    return '/'
  })

  function setUser(newUser: any) {
    user.value = newUser
    isAuthenticated.value = true
    lastCheckTime.value = Date.now()
  }

  const clearAuth = () => {
    user.value = null
    isAuthenticated.value = false
    lastCheckTime.value = null
  }

  const shouldRecheck = () => {
    if (!lastCheckTime.value) return true
    // Recheck if more than 30 seconds have passed
    return Date.now() - lastCheckTime.value > 30000
  }

  return {
    user,
    isUser,
    isAuthenticated: readonly(isAuthenticated),
    isAdmin,
    isJudge,
    isLeader,
    isSponsor,
    isViewer,
    hasEventRole,
    defaultUserPage,
    setUser,
    clearAuth,
    shouldRecheck,
  }
},
{
  persist: {
    storage: persistedState.localStorage,
  },
},
)

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
